import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Target,
  Clock,
  Briefcase,
  Sparkles,
} from "lucide-react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import ChatWidget from "@/components/landing/ChatWidget";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import MobileBottomNav from "@/components/landing/MobileBottomNav";
import SERVICES from "@/data/servicesData";
import useSiteConfig from "@/hooks/useSiteConfig";

/**
 * Per-service slug landing page at /services/:slug.
 *
 * One dedicated, SEO-indexed page per service:
 *   - Unique <h1>, <title>, meta description, OG tags
 *   - JSON-LD Service schema for rich-result eligibility
 *   - Long-form body: proof points, overview, KPIs, deliverables, milestones,
 *     real client engagements, prev/next service navigation
 *
 * Compared to /services (which lists all 16 in one scroll), this route is
 * what Google ranks for long-tail queries like
 *   "meta ads agency dubai" → /services/meta-ads
 *   "saas growth strategy gcc" → /services/saas
 */
export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { config } = useSiteConfig();

  // Find the service + its position so we can render prev/next
  const idx = useMemo(() => SERVICES.findIndex((s) => s.key === slug), [slug]);
  const service = idx >= 0 ? SERVICES[idx] : null;
  const prev = idx > 0 ? SERVICES[idx - 1] : SERVICES[SERVICES.length - 1];
  const next = idx >= 0 && idx < SERVICES.length - 1 ? SERVICES[idx + 1] : SERVICES[0];

  // SEO: swap document title + meta description + OG tags whenever the slug changes
  useEffect(() => {
    if (!service) return undefined;
    const prevTitle = document.title;
    const newTitle = `${service.name} for UAE & GCC brands · Quantum Star Marketing`;
    const newDesc = `${service.tagline} ${stripToLength(service.overview, 140)}`;
    const ogImage = "/og-default.png";
    const url = `${window.location.origin}/services/${service.key}`;

    document.title = newTitle;
    setMeta("description", newDesc);
    setMeta("og:title", newTitle, "property");
    setMeta("og:description", newDesc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", newTitle);
    setMeta("twitter:description", newDesc);
    setLinkRel("canonical", url);

    // JSON-LD Service schema — Google + Bing rich-result eligibility
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "service-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${service.name} — Quantum Star Marketing`,
      serviceType: service.name,
      description: service.overview,
      provider: {
        "@type": "Organization",
        name: "Quantum Star Marketing",
        url: window.location.origin,
        email: "contact@qstarmarketing.com",
      },
      areaServed: ["United Arab Emirates", "Saudi Arabia", "GCC"],
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        url,
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `${service.name} deliverables`,
        itemListElement: service.deliverables.map((d) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: d },
        })),
      },
    });
    document.head.appendChild(ld);

    // Scroll to top on slug change
    window.scrollTo({ top: 0, behavior: "auto" });

    return () => {
      document.title = prevTitle;
      const existing = document.getElementById("service-jsonld");
      if (existing) existing.remove();
    };
  }, [service]);

  // Unknown slug → redirect to /services after a brief notice
  if (!service) {
    return (
      <main className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center px-6">
        <Nav />
        <div className="text-center max-w-md">
          <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
            / 404
          </div>
          <h1 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight">
            Service not found.
          </h1>
          <p className="mt-4 text-zinc-400 text-sm">
            We couldn't find a service called <code className="text-cyan-300">{slug}</code>.
            See our full catalogue instead.
          </p>
          <button
            type="button"
            onClick={() => navigate("/services")}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-3 text-sm font-medium"
            data-testid="service-404-back"
          >
            <ArrowLeft size={14} /> Back to all services
          </button>
        </div>
      </main>
    );
  }

  const num = String(idx + 1).padStart(2, "0");

  return (
    <main className="relative bg-[#050505] text-white selection:bg-cyan-400/20 pb-24 md:pb-0" data-testid={`service-detail-${service.key}`}>
      <Nav />

      {/* HERO — service-specific */}
      <section className="relative pt-28 sm:pt-36 pb-12 sm:pb-16 overflow-hidden noise">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 grid-bg radial-fade opacity-40" />
          <motion.div
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-[10%] h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl"
            aria-hidden
          />
          <motion.div
            animate={{ y: [0, 18, 0], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            className="absolute bottom-0 right-[10%] h-80 w-80 rounded-full bg-purple-600/15 blur-3xl"
            aria-hidden
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-[10px] sm:text-[11px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 mb-5"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="hover:text-cyan-300 transition-colors">Home</Link>
            <span className="text-zinc-700">/</span>
            <Link to="/services" className="hover:text-cyan-300 transition-colors">Services</Link>
            <span className="text-zinc-700">/</span>
            <span className="text-cyan-300">{service.name}</span>
          </nav>

          <div className="flex items-baseline gap-4 sm:gap-5 mb-3">
            <span className="font-mono-acc text-cyan-300 text-sm sm:text-lg tracking-tight shrink-0">
              {num} /
            </span>
            <h1 className="font-clash text-[40px] sm:text-6xl lg:text-[88px] font-semibold tracking-tight leading-[1.02]">
              {service.name}
            </h1>
          </div>
          <p className="text-cyan-300/85 text-base sm:text-lg font-mono-acc max-w-2xl">
            {service.tagline}
          </p>
          <p className="mt-6 max-w-2xl text-[15px] sm:text-base md:text-lg text-zinc-300/90 leading-relaxed">
            {service.overview}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={config.contact.bookingUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="service-hero-book"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-[0_0_40px_-10px_rgba(0,229,255,0.5)]"
            >
              Book a {service.name} strategy call
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <Link
              to="/#audit"
              data-testid="service-hero-audit"
              className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-6 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              Or run a free AI audit
            </Link>
          </div>
        </div>
      </section>

      {/* PROOF + KPIs */}
      <section className="relative py-12 md:py-20 border-y border-white/[0.05] bg-[#070709]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          {service.proofPoints && service.proofPoints.length > 0 && (
            <>
              <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
                / Proof points
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-12">
                {service.proofPoints.slice(0, 3).map((p, i) => (
                  <div
                    key={i}
                    className="rounded-2xl bg-gradient-to-br from-cyan-500/[0.06] via-purple-500/[0.04] to-transparent border border-white/[0.08] p-5"
                  >
                    <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 mb-2">
                      Proof point {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-clash text-lg sm:text-xl font-medium text-white leading-snug">
                      {p}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            {/* Deliverables */}
            <div>
              <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
                <Briefcase size={12} /> What's inside the engagement
              </div>
              <ul className="space-y-2.5">
                {service.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-200 leading-snug" data-selectable>
                    <CheckCircle2 size={14} className="text-cyan-300 mt-0.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* KPIs */}
            <div className="rounded-2xl glass-strong p-5 sm:p-6">
              <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-4">
                <Target size={12} /> Calibrated KPIs
              </div>
              <ul className="space-y-3">
                {service.kpis.map((k, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-200" data-selectable>
                    <span className="font-mono-acc text-cyan-300 text-[10px] mt-1 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="leading-snug">{k}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 90-DAY MILESTONES */}
      <section className="relative py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
            <Clock size={12} /> 90-day shipping plan
          </div>
          <div className="grid md:grid-cols-3 gap-3 md:gap-4">
            {service.milestones.map((m, i) => (
              <div
                key={i}
                className="rounded-2xl glass border border-white/[0.06] p-5"
              >
                <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-2">
                  Phase {String(i + 1).padStart(2, "0")} · {m.day}
                </div>
                <div className="text-sm text-zinc-200 leading-relaxed" data-selectable>{m.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT ENGAGEMENTS — full list, important for SEO body copy */}
      <section className="relative py-12 md:py-20 border-y border-white/[0.05] bg-[#070709]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
            <Sparkles size={12} /> Recent {service.name} engagements
          </div>
          <h2 className="font-clash text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-3xl mb-8">
            Real {service.name.toLowerCase()} work shipped across UAE, KSA & the wider GCC.
          </h2>
          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
            {service.industries.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4 sm:p-5"
              >
                <span className="font-mono-acc text-cyan-300 text-[11px] mt-0.5 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-sm text-zinc-300 leading-relaxed" data-selectable>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION CTA */}
      <section className="relative py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="rounded-3xl bg-gradient-to-br from-purple-500/15 via-cyan-500/[0.08] to-transparent border border-purple-500/25 p-6 sm:p-10 text-center">
            <h2 className="font-clash text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              Ready to ship {service.name} <br className="hidden sm:block" />
              <span className="text-gradient-neon">like this for your business?</span>
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-zinc-300 text-[15px] leading-relaxed">
              30-minute strategy call. No pitch deck. We'll line up what we'd actually do
              in your first 90 days — sized, sequenced, and tied to revenue.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={config.contact.bookingUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-bottom-book"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
              >
                Book the call
                <ArrowRight size={16} />
              </a>
              <Link
                to="/#audit"
                data-testid="service-bottom-audit"
                className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-6 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              >
                Run a free AI audit first
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PREV / NEXT SERVICE NAV */}
      <section className="relative py-10 md:py-16 border-t border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 mb-6 text-center">
            Continue exploring
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to={`/services/${prev.key}`}
              data-testid="service-prev"
              className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-colors flex items-center gap-4"
            >
              <ArrowLeft size={18} className="text-cyan-300 shrink-0 group-hover:-translate-x-1 transition-transform" />
              <div>
                <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  Previous service
                </div>
                <div className="font-clash text-base font-medium text-white group-hover:text-cyan-200 transition-colors">
                  {prev.name}
                </div>
              </div>
            </Link>
            <Link
              to={`/services/${next.key}`}
              data-testid="service-next"
              className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-colors flex items-center justify-end gap-4 text-right"
            >
              <div>
                <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  Next service
                </div>
                <div className="font-clash text-base font-medium text-white group-hover:text-cyan-200 transition-colors">
                  {next.name}
                </div>
              </div>
              <ArrowRight size={18} className="text-cyan-300 shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/services"
              data-testid="service-back-all"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              ← Back to all 16 services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
      <WhatsAppButton />
      <MobileBottomNav />
    </main>
  );
}

/* --------------------------------- helpers -------------------------------- */

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkRel(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function stripToLength(s, n) {
  if (!s) return "";
  const trimmed = s.replace(/\s+/g, " ").trim();
  return trimmed.length <= n ? trimmed : trimmed.slice(0, n - 1).trim() + "…";
}
